import React from 'react';
import { useTranslation } from 'react-i18next';
import CurrencyTagsInput from '../../../components/form/currency-tags-input.jsx';
import {
  FilterPresetList,
  FilterPresetNameModal,
  FilterPresetSaveButton,
} from '../../../shared/filter-presets/filter-presets.jsx';
import AssignmentModal from '../../../shared/ui/assignment-modal/assignment-modal.jsx';
import EntityLink from '../../../shared/ui/entity-link/entity-link.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import { getEnumOptionLabel } from '../../../shared/i18n/enum-labels.js';
import DocumentDetailsModal from '../../profiles/ui/document-details-modal.jsx';

const DOCUMENT_STATUS_OPTIONS = [
  'new',
  'approved',
  'rejected',
  'pending',
];

function getAriaSort(sort, field) {
  if (sort?.sortBy !== field) {
    return 'none';
  }

  return sort?.sortOrder === 'asc' ? 'ascending' : 'descending';
}

function SortableHeader({ field, label, sort, onSortChange }) {
  const isActive = sort?.sortBy === field;
  const iconClass = isActive
    ? (sort?.sortOrder === 'asc' ? 'ti-arrow-up' : 'ti-arrow-down')
    : 'ti-arrows-sort';

  return (
    <button
      type="button"
      className={`btn btn-link p-0 text-decoration-none fw-semibold ${isActive ? 'text-primary' : 'text-muted'}`}
      onClick={() => onSortChange(field)}
    >
      <span>{label}</span>
      <i className={`ti ${iconClass} ms-1`} aria-hidden="true"></i>
    </button>
  );
}

function DocumentsPageView({
  rows,
  filters,
  sort,
  statusOptions,
  typeOptions,
  countryOptions,
  isFilterOpen,
  isLoading,
  isLoadingMore,
  hasMoreDocuments,
  error,
  selectedDocument,
  documentViewer,
  documentForm,
  isDocumentSaving,
  documentSaveError,
  documentSaveSuccess,
  isAssignModalOpen,
  assignTargetProfileName,
  adminProfileOptions,
  isAdminProfilesLoading,
  selectedManagerId,
  isAssignmentSaving,
  assignmentError,
  changeSort,
  updateFilter,
  onCountriesFilterChange,
  resetAndCloseFilters,
  setIsFilterOpen,
  openAssignModal,
  closeAssignModal,
  onSelectManager,
  assignManager,
  unassignManager,
  openDetails,
  closeDetails,
  onDocumentFormChange,
  saveSelectedDocument,
  loadMoreDocuments,
  filterPresets,
  isFilterPresetModalOpen,
  filterPresetName,
  filterPresetNameError,
  openSaveFilterPreset,
  closeSaveFilterPreset,
  setFilterPresetName,
  saveFilterPreset,
  applyFilterPreset,
  deleteFilterPreset,
}) {
  const { t } = useTranslation();
  const isTrueLike = (value) => {
    if (value === true || value === 1) {
      return true;
    }

    const normalized = String(value || '').trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  };

  const getStatusBadgeClass = (statusValue) => {
    const status = String(statusValue || '').trim().toLowerCase();
    const map = {
      approved: 'bg-success-subtle text-success',
      verified: 'bg-success-subtle text-success',
      pending: 'bg-warning-subtle text-warning',
      review: 'bg-warning-subtle text-warning',
      rejected: 'bg-danger-subtle text-danger',
      declined: 'bg-danger-subtle text-danger',
      expired: 'app-badge-disabled',
      uploaded: 'bg-info-subtle text-info',
      submitted: 'bg-primary-subtle text-primary',
    };

    return map[status] || 'app-badge-disabled';
  };

  const getTypeBadgeClass = (typeValue) => {
    const type = String(typeValue || '').trim().toLowerCase();
    const map = {
      'id-front': 'bg-primary-subtle text-primary',
      'id-back': 'bg-primary-subtle text-primary',
      poa: 'bg-info-subtle text-info',
      other: 'app-badge-disabled',
    };

    return map[type] || 'app-badge-disabled';
  };

  const getTranslationBadgeMeta = (row) => {
    if (isTrueLike(row.translationRequired)) {
      return { label: 'PENDING', className: 'bg-warning-subtle text-warning' };
    }

    const translation = String(row.translation || '').trim();
    if (translation && translation !== '-') {
      return { label: 'DONE', className: 'bg-success-subtle text-success' };
    }

    return { label: 'N/A', className: 'app-badge-disabled' };
  };

  return (
    <div>
      <FloatingAlerts error={error} />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('documents.title')}</h2>
          <p>{t('documents.subtitle')}</p>
        </div>
        <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end">
          <div className="form-check form-switch mb-0">
            <input
              id="documentsAssignedOnly"
              type="checkbox"
              name="assignedOnly"
              className="form-check-input"
              checked={filters.assignedOnly}
              onChange={updateFilter}
            />
            <label htmlFor="documentsAssignedOnly" className="form-check-label">
              {t('profiles.mineOnly')}
            </label>
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={() => setIsFilterOpen((prev) => !prev)}
          >
            <i className="ti ti-adjustments-horizontal me-1 fs-5"></i>
            {t('profiles.filters')}
          </button>
        </div>
      </div>

      <div className="widget-content searchable-container list">
        {isFilterOpen ? (
          <div className="card card-body">
            <div className="row g-2">
              <div className="col-xl-3 col-md-6">
                <input
                  type="search"
                  name="name"
                  className="form-control"
                  value={filters.name}
                  onChange={updateFilter}
                  placeholder={t('documents.filterName')}
                />
              </div>
              <div className="col-xl-3 col-md-6">
                <select
                  name="status"
                  className="form-select"
                  value={filters.status}
                  onChange={updateFilter}
                >
                  <option value="">{t('documents.allStatuses')}</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {getEnumOptionLabel(t, 'documentStatus', status)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-xl-3 col-md-6">
                <select
                  name="type"
                  className="form-select"
                  value={filters.type}
                  onChange={updateFilter}
                >
                  <option value="">{t('documents.allTypes')}</option>
                  {typeOptions.map((type) => (
                    <option key={type} value={type}>
                      {getEnumOptionLabel(t, 'documentType', type)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-xl-2 col-md-4 d-flex align-items-center">
                <div className="form-check form-switch mt-2">
                  <input
                    id="translationRequiredFilter"
                    type="checkbox"
                    name="translationRequired"
                    className="form-check-input"
                    checked={filters.translationRequired}
                    onChange={updateFilter}
                  />
                  <label htmlFor="translationRequiredFilter" className="form-check-label">
                    {t('documents.translationRequired')}
                  </label>
                </div>
              </div>
              <div className="col-xl-1 col-md-2">
                <div className="d-flex gap-2">
                  <FilterPresetSaveButton onClick={openSaveFilterPreset} className="flex-fill" />
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    aria-label={t('documents.clearAndClose')}
                    onClick={resetAndCloseFilters}
                  >
                    <i className="ti ti-x" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
              <div className="col-12">
                <label className="form-label mb-1">{t('profiles.countries')}</label>
                <CurrencyTagsInput
                  value={filters.countriesOnly}
                  options={countryOptions}
                  onChange={onCountriesFilterChange}
                  placeholderText="Select one or more countries"
                />
              </div>
            </div>
            <FilterPresetList presets={filterPresets} onApply={applyFilterPreset} onDelete={deleteFilterPreset} />
          </div>
        ) : null}
        <div className={`card card-body${isFilterOpen ? ' mt-3' : ''}`}>
          <LoadingContainer isLoading={isLoading} hasData={rows.length > 0}>
            <div className="table-responsive">
              <table className="table search-table align-middle text-nowrap mb-0">
              <thead className="header-item">
                <tr>
                  <th aria-sort={getAriaSort(sort, 'name')}>
                    <SortableHeader field="name" label={t('common.name')} sort={sort} onSortChange={changeSort} />
                  </th>
                  <th aria-sort={getAriaSort(sort, 'type')}>
                    <SortableHeader field="type" label={t('common.type')} sort={sort} onSortChange={changeSort} />
                  </th>
                  <th aria-sort={getAriaSort(sort, 'status')}>
                    <SortableHeader field="status" label={t('common.status')} sort={sort} onSortChange={changeSort} />
                  </th>
                  <th>{t('documents.profile')}</th>
                  <th>{t('documents.translation')}</th>
                  <th aria-sort={getAriaSort(sort, 'created')}>
                    <SortableHeader field="created" label={t('documents.created')} sort={sort} onSortChange={changeSort} />
                  </th>
                  <th aria-sort={getAriaSort(sort, 'modified')}>
                    <SortableHeader field="modified" label={t('documents.modified')} sort={sort} onSortChange={changeSort} />
                  </th>
                  <th aria-sort={getAriaSort(sort, 'expirationDate')}>
                    <SortableHeader field="expirationDate" label={t('documents.expiration')} sort={sort} onSortChange={changeSort} />
                  </th>
                  <th>{t('profiles.assignedTo')}</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center text-muted">
                      {t('documents.noDocuments')}
                    </td>
                  </tr>
                ) : null}

                {rows.map((row) => (
                  <tr key={row.key} className="app-clickable-row" onClick={() => openDetails(row.key)}>
                    <td>{row.name}</td>
                    <td>
                      <span className={`badge rounded-pill fw-semibold text-uppercase ${getTypeBadgeClass(row.type)}`}>
                        {row.type || '-'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge rounded-pill fw-semibold text-uppercase ${getStatusBadgeClass(row.status)}`}>
                        {row.status || '-'}
                      </span>
                    </td>
                    <td>
                      {row.profileId ? (
                        <EntityLink
                          to={`/profiles/${encodeURIComponent(row.profileId)}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {row.profileName}
                        </EntityLink>
                      ) : (
                        row.profileName
                      )}
                    </td>
                    <td>
                      {(() => {
                        const translationMeta = getTranslationBadgeMeta(row);
                        return (
                          <span className={`badge rounded-pill fw-semibold text-uppercase ${translationMeta.className}`}>
                            {translationMeta.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td>{row.createdAt}</td>
                    <td>{row.modifiedAt}</td>
                    <td>{row.expirationAt}</td>
                    <td>
                      <button
                        type="button"
                        className={`btn btn-sm ${row.assignedToName && row.assignedToName !== '-' ? 'btn-outline-primary' : 'btn-primary'}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          openAssignModal(row);
                        }}
                        disabled={!row.profileId}
                      >
                        {row.assignedToName && row.assignedToName !== '-' ? row.assignedToName : t('common.assign')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </LoadingContainer>
          {hasMoreDocuments && !isLoading ? (
            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={loadMoreDocuments}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? t('common.loading') : t('documents.loadMore')}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <AssignmentModal
        isOpen={isAssignModalOpen}
        title={t('profiles.assignProfileManager')}
        targetLabel={t('profiles.profile')}
        targetName={assignTargetProfileName}
        error={assignmentError}
        options={adminProfileOptions}
        isOptionsLoading={isAdminProfilesLoading}
        selectedId={selectedManagerId}
        isSaving={isAssignmentSaving}
        showUnassign
        onClose={closeAssignModal}
        onSelect={onSelectManager}
        onAssign={assignManager}
        onUnassign={unassignManager}
      />

      {selectedDocument ? (
        <DocumentDetailsModal
          selectedDocument={selectedDocument}
          documentViewer={documentViewer}
          documentForm={documentForm}
          onDocumentFormChange={onDocumentFormChange}
          isDocumentSaving={isDocumentSaving}
          documentSaveError={documentSaveError}
          documentSaveSuccess={documentSaveSuccess}
          documentStatusOptions={DOCUMENT_STATUS_OPTIONS}
          onClose={closeDetails}
          onSave={saveSelectedDocument}
        />
      ) : null}
      <FilterPresetNameModal
        isOpen={isFilterPresetModalOpen}
        name={filterPresetName}
        error={filterPresetNameError}
        onNameChange={setFilterPresetName}
        onSubmit={saveFilterPreset}
        onClose={closeSaveFilterPreset}
      />
    </div>
  );
}

export default DocumentsPageView;
